import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SuperAdminIcon from './SuperAdminIcon';

const getInitials = (user) => {
  const value = user?.name || user?.email || 'Super Admin';
  return value.split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase();
};

const SuperAdminTopbar = ({ breadcrumbs, onLogout, onOpenMenu, onOpenSearch, searchButtonRef, user }) => {
  const navigate = useNavigate();
  const identityRef = useRef(null);
  const identityButtonRef = useRef(null);
  const [identityOpen, setIdentityOpen] = useState(false);
  const environment = process.env.REACT_APP_ENVIRONMENT
    || process.env.REACT_APP_APP_ENV
    || (process.env.NODE_ENV === 'development' ? 'Local' : '');
  const displayName = user?.name || user?.email || 'Super Admin';

  useEffect(() => {
    if (!identityOpen) return undefined;
    const onPointerDown = (event) => {
      if (!identityRef.current?.contains(event.target)) setIdentityOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIdentityOpen(false);
        identityButtonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [identityOpen]);

  const signOut = () => {
    setIdentityOpen(false);
    onLogout();
    navigate('/sa/login', { replace: true });
  };

  return (
    <header className="sa-topbar">
      <div className="sa-topbar__leading">
        <button className="sa-icon-button sa-topbar__menu" type="button" onClick={onOpenMenu} aria-label="Open navigation">
          <SuperAdminIcon name="menu" />
        </button>
        <nav aria-label="Breadcrumb" className="sa-breadcrumbs">
          <ol>
            {breadcrumbs.map((crumb, index) => (
              <li key={`${crumb.label}-${index}`} aria-current={index === breadcrumbs.length - 1 ? 'page' : undefined}>
                {crumb.to && index !== breadcrumbs.length - 1 ? <Link to={crumb.to}>{crumb.label}</Link> : <span>{crumb.label}</span>}
              </li>
            ))}
          </ol>
        </nav>
      </div>

      <div className="sa-topbar__actions">
        <button ref={searchButtonRef} className="sa-search-trigger" type="button" onClick={onOpenSearch} aria-label="Search Super Admin destinations">
          <SuperAdminIcon name="search" size={18} />
          <span>Search</span>
          <kbd>⌘ K</kbd>
        </button>
        {environment ? <span className={`sa-environment sa-environment--${environment.toLowerCase()}`}>{environment}</span> : null}
        <div className="sa-admin-menu" ref={identityRef}>
          <button
            ref={identityButtonRef}
            className="sa-admin-identity"
            type="button"
            aria-haspopup="menu"
            aria-expanded={identityOpen}
            onClick={() => setIdentityOpen((current) => !current)}
          >
            <span className="sa-admin-identity__avatar" aria-hidden="true">{getInitials(user)}</span>
            <span className="sa-admin-identity__copy">
              <strong>{displayName}</strong>
              <small>Super Admin</small>
            </span>
            <SuperAdminIcon className="sa-admin-identity__chevron" name="chevronDown" size={15} />
          </button>
          {identityOpen ? (
            <div className="sa-popover sa-admin-popover" role="menu" aria-label="Super Admin account">
              <div className="sa-admin-popover__summary">
                <strong>{displayName}</strong>
                {user?.email && user.email !== displayName ? <span>{user.email}</span> : null}
                <small>Super Admin</small>
              </div>
              <Link role="menuitem" to="/profile" onClick={() => setIdentityOpen(false)}>
                <SuperAdminIcon name="profile" size={17} /> Account profile
              </Link>
              <Link role="menuitem" to="/dashboard/individual/home" onClick={() => setIdentityOpen(false)}>
                <SuperAdminIcon name="external" size={17} /> Main CrickArena
              </Link>
              <button role="menuitem" type="button" onClick={signOut}>
                <SuperAdminIcon name="logout" size={17} /> Sign out
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
};

export default SuperAdminTopbar;
